import React, { forwardRef, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import Grid from '@material-ui/core/Grid'

import farmSchema from '@galo-monitor/forms/schemas/company/farm.schema'
import { useT } from '@galo-monitor/i18n'
import galoMonitorApi from '@galo-monitor/services/apis/galoMonitorApi'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { FarmActions } from '@galo-monitor/stores/farm'
import Form from '@galo-monitor/web-components/Form'
import InputEmail from '@galo-monitor/web-components/InputEmail'
import InputPhone from '@galo-monitor/web-components/InputPhone'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import CustomInputSearch from '@galo-monitor/web-containers/forms/property/ActivityForm/components/CustomInputSearch'

import { Container } from './styles'

const FarmForm = forwardRef((props, formRef) => {
  const { farm, loading, setLoading, onSuccess, onError } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: plans, isValidating } = useGaloMonitorApi('/companies/plans')

  const planOptions = useMemo(
    () => map(plans, plan => ({
      label: plan.name,
      value: plan.id.toString()
    })),
    [plans]
  )

  const handleFetchSegmentOptions = params => galoMonitorApi
    .get('/companies/farms/segments', { params })

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)
      dispatch(FarmActions.saveFarm(
        {
          ...farm,
          ...data
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('{this} account', {
              howMany: 1,
              this: t('farm', { howMany: 1 })
            }),
            action: toLower(t(isEmpty(farm) ? 'created' : 'updated', { gender: 'female', howMany: 1 }))
          }))
          onSuccess(data)
        },
        onError
      ))
    },
    [dispatch, farm, onError, onSuccess, setLoading, snackbar, t]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ farmSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputText
              name="name"
              label={ t('name of account') }
              defaultValue={ farm.name }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputText
              name="name_responsible"
              label={ `${ t('responsible') } (${ t('client') })` }
              defaultValue={ farm.nameResponsible }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputPhone
              name="phone_responsible"
              label={ t('main phone') }
              defaultValue={ farm.phoneResponsible }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputEmail
              name="email_responsible"
              label={ t('email') }
              defaultValue={ farm.emailResponsible }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <CustomInputSearch
              name="segment_name"
              label={ t('segment', { howMany: 1 }) }
              options={ handleFetchSegmentOptions }
              defaultValue={ farm.segment?.name ?? farm.segment ?? '' }
              disabled={ loading }
              asyncOptionValueField="name"
              creatable
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 6 }>
            <InputSelect
              name="plan_id"
              label={ t('plan', { howMany: 1 }) }
              defaultValue={ farm.planId?.toString() ?? '' }
              disabled={ loading }
              loading={ isValidating }
              options={ planOptions }
              fullWidth
            />
          </Grid>
        </Grid>
      </Container>
    </Form>
  )
})

FarmForm.propTypes = {
  farm: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

FarmForm.defaultProps = {
  farm: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default FarmForm
